import React, { useRef } from 'react';
import { contentBundles } from '../../data/mock';
import * as Icons from 'lucide-react';

// Media assets for content bundles
const mediaAssets = {
  luxury: {
    video1: "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/9uye1vby_70k.mp4",
    image1: "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/mrlhpjl4_70k.webp",
    video2: "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/ebe4hnb1_70kk.mp4",
    image2: "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/5a9l3whd_70kkk.webp",
    video3: "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/k0yvf1xd_70kkkk.mp4",
  },
  new: {
    showcase1: "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/vg7pzw4z_20k.avif",
    showcase2: "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/2oe0sw0r_20kk.jpg",
    showcase3: "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/upui6ymy_20kkk.avif",
    showcaseVideo: "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/qs4ifyrp_20kkl.mp4",
  }
};

const VideoCard = ({ src, className = "" }) => {
  const videoRef = useRef(null);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
      />
      {/* Live indicator */}
      <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
        <span className="text-xs text-white font-medium">PREVIEW</span>
      </div>
    </div>
  );
};

const ContentLibrarySection = () => {
  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent size={24} /> : null;
  };

  const totalCount = "183,300+";

  return (
    <section id="content-library" className="py-24 px-6 bg-[#0f0f0f]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4 block">
            Content Library Access
          </span>
          <h2 className="heading-2 text-white mb-4">
            {totalCount} Videos
            <br />
            <span className="text-white/60">Ready to Post</span>
          </h2>
          <p className="body-medium text-[#737373] max-w-2xl mx-auto">
            Licensed access to a massive content library. All optimized for TikTok, Instagram Reels, and YouTube Shorts.
          </p>
        </div>

        {/* Main Bento Grid with Media */}
        <div className="grid grid-cols-12 gap-4 mb-8">
          
          {/* Luxury Lifestyle - Large Card with IMAGE (not video) */}
          <div className="col-span-12 md:col-span-6 lg:col-span-5 row-span-2">
            <div className="relative h-full min-h-[480px] overflow-hidden bg-[#141414] border border-white/10 rounded-3xl group hover:border-white/20 transition-all duration-300">
              <img 
                src={mediaAssets.luxury.image1} 
                alt="Luxury lifestyle content" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              {/* Content Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center text-amber-400">
                    {getIcon('Crown')}
                  </div>
                </div>
                <div className="text-4xl font-bold text-white mb-1">70,000+</div>
                <h3 className="text-xl font-semibold text-white mb-2">Luxury & Rich Lifestyle</h3>
                <p className="text-white/60 text-sm">Cars • Yachts • Private Jets • Millionaire Lifestyle</p>
              </div>
            </div>
          </div>

          {/* Viral Short Videos */}
          <div className="col-span-12 md:col-span-6 lg:col-span-4">
            <div className="relative h-full min-h-[230px] overflow-hidden bg-[#141414] border border-white/10 rounded-3xl group hover:border-white/20 transition-all duration-300">
              <VideoCard 
                src={mediaAssets.luxury.video2} 
                className="absolute inset-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center text-red-400">
                    {getIcon('Flame')}
                  </div>
                </div>
                <div className="text-3xl font-bold text-white mb-1">100,000+</div>
                <h3 className="text-lg font-semibold text-white">Viral Short Videos</h3>
                <p className="text-white/60 text-xs mt-1">Satisfying • Gaming • AI Animated • Funny • Pets • Gym</p>
              </div>
            </div>
          </div>

          {/* Motivational - Small Card */}
          <div className="col-span-6 lg:col-span-3">
            <div className="relative h-full min-h-[230px] overflow-hidden bg-[#141414] border border-white/10 rounded-3xl group hover:border-white/20 transition-all duration-300">
              <img 
                src={mediaAssets.new.showcase3} 
                alt="Motivational content" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                    {getIcon('TrendingUp')}
                  </div>
                </div>
                <div className="text-2xl font-bold text-white">12,000+</div>
                <h3 className="text-sm font-semibold text-white">Motivational & Business</h3>
                <p className="text-white/60 text-xs mt-1">Faceless • Inspirational • Quotes</p>
              </div>
            </div>
          </div>

          {/* Ready-to-Post */}
          <div className="col-span-6 lg:col-span-4">
            <div className="relative h-full min-h-[230px] overflow-hidden bg-[#141414] border border-white/10 rounded-3xl group hover:border-white/20 transition-all duration-300">
              <img 
                src={mediaAssets.luxury.image2} 
                alt="Ready to post reels" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400">
                    {getIcon('Rocket')}
                  </div>
                </div>
                <div className="text-2xl font-bold text-white">1,300+</div>
                <h3 className="text-sm font-semibold text-white">Ready-to-Post Reels</h3>
                <p className="text-white/60 text-xs mt-1">PLR / MRR Licensed</p>
              </div>
            </div>
          </div>

          {/* Additional Video Preview */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <div className="relative h-full min-h-[230px] overflow-hidden bg-[#141414] border border-white/10 rounded-3xl group hover:border-white/20 transition-all duration-300">
              <VideoCard 
                src={mediaAssets.luxury.video3} 
                className="absolute inset-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-lg font-semibold text-white">Premium Quality</div>
                <p className="text-white/60 text-sm mt-1">4K Ultra HD • All Platforms</p>
              </div>
            </div>
          </div>

        </div>

        {/* Platform Tags */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <span className="text-sm text-[#737373]">Optimized for:</span>
          {['TikTok', 'Instagram Reels', 'YouTube Shorts'].map((platform) => (
            <span 
              key={platform}
              className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/80"
            >
              {platform}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContentLibrarySection;
